import { escapeHtml } from './escapeHtml';
import { companyHeader } from './company';
import { isPieceUnit } from './units';

// Sub-order statuses at or past loading. Until then a print shows ORDERED values;
// from here on it shows ACTUAL values (decision 2026-10-19).
const LOADED_STATUSES = ['loaded', 'in_transit', 'delivered', 'ready_for_challan'];

export const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  return `₹ ${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
};

export const printDocument = (data, documentTitle = 'Document') => {
  if (!data) return;

  // HTML-escaped: everything below is interpolated into document.write HTML (P1-2).
  const safe = (v) => escapeHtml(v);
  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '');
  const fmtDT = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

  const isDeliveryOrder = documentTitle === 'Delivery Order';
  // Only a sub-order carries loading state. An order print (status 'delivered',
  // 'invoiced', ...) has no actual fields at all, so it always shows ordered values.
  const isSubOrder = !!data.sub_order_number;
  const loadingDone = isSubOrder && LOADED_STATUSES.includes(String(data.status || '').toLowerCase());

  const dueDate = data.due_date ? fmt(data.due_date) : null;
  const historyHtml = (data.supervisor_history && data.supervisor_history.length > 1) ? `
    <div style="margin-bottom: 8px;">
      <div style="font-weight: 700; font-size: 8px; text-transform: uppercase; margin-bottom: 2px;">Supervisor History</div>
      <table style="border: none; margin-bottom: 0;">
        <thead style="border-bottom: 1px solid #eee;">
          <tr style="background: none;">
            <th style="border: none; background: none; padding: 1px; font-size: 7px; color: #666;">Supervisor</th>
            <th style="border: none; background: none; padding: 1px; font-size: 7px; color: #666;">Assigned By</th>
            <th style="border: none; background: none; padding: 1px; font-size: 7px; color: #666;">Time</th>
          </tr>
        </thead>
        <tbody>
          ${data.supervisor_history.map(h => `
            <tr>
              <td style="border: none; padding: 1px; font-size: 7px;">${safe(h.supervisor_name)}</td>
              <td style="border: none; padding: 1px; font-size: 7px;">${safe(h.assigned_by_name)}</td>
              <td style="border: none; padding: 1px; font-size: 7px;">${fmtDT(h.assigned_at)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  ` : '';

  const items = Array.isArray(data.items) ? data.items.filter(it => it && Object.keys(it).length > 0) : [];
  const hasCoilLength = items.some(it => it?.coil_tracked && safe(it.length_note).trim());

  const itemsRows = items.map((it, idx) => {
    if (isDeliveryOrder) {
      const qtyInPieces = safe(it.quantity_in_pieces ?? it.actual_pieces ?? '');
      const orderedQty = safe(it.order_item_quantity ?? it.quantity ?? '');
      const unit = safe(it.unit);

      // Ordered values until loading completes, actual values after.
      const actualPieces = Number(it.actual_pieces) || 0;
      const displayPcs = loadingDone ? actualPieces : (Number(it.quantity_in_pieces) || '');

      const actualWt = Number(it.actual_weight) || 0;
      const orderedWt = Number(it.order_item_quantity ?? it.quantity) || 0;
      const displayTotalWt = loadingDone ? actualWt : (orderedWt || '');

      const pcsForAvg = Number(displayPcs) || 0;
      const wtForAvg = displayTotalWt ? Number(displayTotalWt) : 0;
      const displayAvg = pcsForAvg > 0 && wtForAvg > 0 ? (wtForAvg / pcsForAvg).toFixed(2) : '';

      const itemRate = it.price || 0;
      const itemAmount = Number(it.amount) || 0;
      const itemGst = Number(it.gst_amount) || 0;
      const lengthNote = it.coil_tracked && safe(it.length_note).trim()
        ? safe(it.length_note).trim()
        : '';

      return `
      <tr>
        <td>${idx + 1}</td>
        <td>
          <div style="font-weight: 700; font-size: 9px;">${safe(it.size_name || it.product_name)}</div>
        </td>
        <td>${qtyInPieces}</td>
        <td>${orderedQty} ${unit}</td>
        <td>${displayPcs}</td>
        <td>${displayAvg}</td>
        <td>${displayTotalWt ? displayTotalWt + ' ' + unit : ''}</td>
        <td class="text-right">${itemRate ? formatCurrency(itemRate) : ''}</td>
        <td class="text-right">${itemGst ? formatCurrency(itemGst) : ''}</td>
        <td class="text-right">${itemAmount ? formatCurrency(itemAmount) : ''}</td>
        ${hasCoilLength ? `<td style="width: 60px; max-width: 60px; white-space: normal; overflow-wrap: anywhere; word-break: break-word;">${lengthNote || '-'}</td>` : ''}
        <td>${safe(it.remark).trim() || '-'}</td>
      </tr>`;
    } else {
      const itemAmount = Number(it.amount) || 0;
      const gstAmount = Number(it.gst_amount) || 0;

      // Ordered values until loading completes, actual values after (2026-10-19).
      const displayPieces = safe(loadingDone ? (it.actual_pieces ?? '') : (it.quantity_in_pieces ?? it.actual_pieces ?? ''));

      const orderQtyValue = it.order_item_quantity !== undefined ? it.order_item_quantity : it.quantity;
      const orderQty = safe(orderQtyValue !== undefined ? Math.ceil(parseFloat(orderQtyValue)) : '');

      // A staff pcs line keeps its optional kg WEIGHT in `quantity` (the count is in
      // quantity_in_pieces and shown in the PCS column), so ASSIGNED/ACTUAL are kg for
      // it. Its `actual_quantity` is a piece count, so only actual_weight is a weight.
      const hasPieceCount = it.quantity_in_pieces !== null && it.quantity_in_pieces !== undefined && it.quantity_in_pieces !== '';
      const pcsLine = isPieceUnit(it.unit) && hasPieceCount;
      const qtyUnit = pcsLine ? 'kg' : safe(it.unit);
      const actualQtyValue = !loadingDone ? orderQtyValue
        : pcsLine ? (it.actual_weight ?? 0)
        : (it.actual_weight ?? it.actual_quantity ?? 0);
      const actualQty = safe(actualQtyValue !== undefined ? Math.ceil(parseFloat(actualQtyValue)) : '');

      return `
      <tr>
        <td>${idx + 1}</td>
        <td>
          <div style="font-weight: 700; font-size: 9px;">${safe(it.size_name || it.product_name)}</div>
        </td>
        <td>${safe(it.hsn_code)}</td>
        <td>${displayPieces}</td>
        <td>${orderQty}${orderQty ? ' ' + qtyUnit : ''}</td>
        <td>${actualQty}${actualQty ? ' ' + qtyUnit : ''}</td>
        <td class="text-right">${formatCurrency(it.price || 0)}</td>
        <td class="text-right">${formatCurrency(gstAmount)}</td>
        <td class="text-right">${formatCurrency(itemAmount)}</td>
        ${hasCoilLength ? `<td style="width: 60px; max-width: 60px; white-space: normal; overflow-wrap: anywhere; word-break: break-word;">${it.coil_tracked && safe(it.length_note).trim() ? safe(it.length_note).trim() : '-'}</td>` : ''}
        <td>${safe(it.remark).trim() || '-'}</td>
      </tr>`;
    }
  }).join('\n');

  const totalItemAmount = Number(data.subtotal) || 0;
  const loading = Number(data.total_loading_cost) || 0;
  const loadingGst = Number(data.loading_cost_gst_amount) || 0;

  const freight = Number(data.freight) || 0;
  const freightGst = Number(data.freight_gst_amount) || 0;
  const totalGst = Number(data.total_gst_amount) || 0;
  const netTotal = Number(data.total_amount) || 0;

  // Freight type billing logic
  const freightTypePrint = data.freight_type || '';
  const isFreightChargeablePrint = freightTypePrint === 'freight_chargeable';
  const isFreightToPayPrint = freightTypePrint === 'free_delivery_freight_to_pay';
  const showFreightRowPrint = !!freightTypePrint;
  const freightPricingTypePrint = data.freight_pricing_type || '';
  const freightLabelPrint = isFreightChargeablePrint
    ? `Freight Chargeable${freightPricingTypePrint === 'per_kg' ? ' (Per KG)' : freightPricingTypePrint === 'fixed' ? ' (Fixed)' : ''}`
    : isFreightToPayPrint ? 'Freight To Pay'
    : freightTypePrint === 'ex_warehouse' ? 'Ex Warehouse'
    : freightTypePrint === 'free_delivery_by_supplier' ? 'Free by Supplier'
    : freightTypePrint === 'free_delivery_paid_by_company' ? 'Freight Paid by Company'
    : 'Freight';
  const freightValuePrint = isFreightToPayPrint
    ? `- ${formatCurrency(freight)}`
    : `${formatCurrency(freight)}`;
  const freightColorPrint = isFreightToPayPrint ? 'color: #059669;' : (!isFreightChargeablePrint ? 'color: #9ca3af;' : '');

  const finalTruckWeightDisplay = data.final_truck_weight ? `${safe(data.final_truck_weight)} KG` : '';
  const emptyTruckWeightDisplay = data.empty_truck_weight ? `${safe(data.empty_truck_weight)} KG` : '';

  // Name from either customer_name or tally_name
  const nameDisplay = safe(data.customer_name || data.tally_name);
  const locationDisplay = safe(data.location_name || data.delivery_location_name);
  const documentNumber = safe(data.sub_order_number || data.order_number || data.challan_number || data.advice_number || data.po_number);

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${documentTitle} - ${documentNumber}</title>
<style>
  @page { size: A5; margin: 8mm; }
  * { box-sizing: border-box; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; color: #333; }
  body { margin: 0; padding: 0; background: #fff; line-height: 1.2; font-size: 9px; }
  .no-print { padding: 10px; background: #f0f0f0; border-bottom: 1px solid #ddd; }
  .print-container { max-width: 148mm; margin: 0 auto; padding: 8mm; }
  .company-header { text-align: center; font-size: 16px; font-weight: 800; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 1px; color: #000; }
  .title { text-align: center; font-size: 12px; font-weight: 700; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
  .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; margin-bottom: 8px; font-size: 8px; }
  .header-field { display: flex; justify-content: flex-start; gap: 4px; margin-bottom: 1px; }
  .header-label { font-weight: 600; min-width: 50px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6px; font-size: 7px; border: 1px solid #ddd; }
  th, td { padding: 2px 3px; text-align: left; border: 1px solid #ddd; }
  th { background: #f5f5f5; font-weight: 700; color: #555; text-transform: uppercase; font-size: 6px; letter-spacing: 0.3px; }
  .text-right { text-align: right; }
  .footer-section { display: grid; grid-template-columns: 1.5fr 1fr; gap: 8px; margin-top: 6px; align-items: flex-start; font-size: 7px; }
  .total-qty-row { font-size: 8px; font-weight: 700; margin-bottom: 4px; }
  .summary-table { width: 100%; border: none; }
  .summary-table td { padding: 2px 0; border: none; font-size: 8px; }
  .summary-table .label { color: #555; }
  .summary-table .value { text-align: right; font-weight: 600; }
  .summary-table .net-total-row td { border-top: 2px solid #333; font-weight: 800; font-size: 9px; padding-top: 4px; }
  @media print { .no-print { display: none !important; } .print-container { padding: 0; } body { font-size: 9px; } }
</style>
</head>
<body>
<div class="no-print"><button onclick="window.print()" style="padding: 10px 20px; cursor:pointer;">Print ${documentTitle}</button></div>
<div class="print-container">
  <div class="company-header">${safe(companyHeader(data.warehouse_name))}</div>
  ${[process.env.REACT_APP_COMPANY_ADDRESS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_ADDRESS}</div>` : '', (process.env.REACT_APP_COMPANY_GST || process.env.REACT_APP_COMPANY_PAN) ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${[process.env.REACT_APP_COMPANY_GST ? 'GST: ' + process.env.REACT_APP_COMPANY_GST : '', process.env.REACT_APP_COMPANY_PAN ? 'PAN: ' + process.env.REACT_APP_COMPANY_PAN : ''].filter(Boolean).join(' | ')}</div>` : '', process.env.REACT_APP_COMPANY_BANK_DETAILS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_BANK_DETAILS}</div>` : ''].filter(Boolean).join('')}
  <div class="title">${documentTitle}</div>
  <div class="header-grid">
    ${nameDisplay ? `<div class="header-field"><span class="header-label">Name:</span> <span>${nameDisplay}</span></div>` : ''}
    ${(data.date || data.created_at) ? `<div class="header-field"><span class="header-label">Date:</span> <span>${fmt(data.date || data.created_at)}</span></div>` : ''}
    ${(data.order_price_list_date || data.price_list_date) ? `<div class="header-field"><span class="header-label">Price List Date:</span> <span>${fmt(data.order_price_list_date || data.price_list_date)}</span></div>` : ''}
    ${(data.order_created_at || data.created_at) ? `<div class="header-field"><span class="header-label">Booking Date:</span> <span>${fmt(data.order_created_at || data.created_at)}</span></div>` : ''}
    ${locationDisplay ? `<div class="header-field"><span class="header-label">Location:</span> <span>${locationDisplay}</span></div>` : ''}
    ${documentNumber ? `<div class="header-field"><span class="header-label">Number:</span> <span>${documentNumber}</span></div>` : ''}
    ${data.vehicle_number ? `<div class="header-field"><span class="header-label">Truck Number:</span> <span>${safe(data.vehicle_number)}</span></div>` : ''}
    ${data.driver_contact ? `<div class="header-field"><span class="header-label">Driver Contact:</span> <span>${safe(data.driver_contact)}</span></div>` : ''}
    ${data.sdo_created_by_name ? `<div class="header-field"><span class="header-label">Sub Delivery Order Created by:</span> <span>${safe(data.sdo_created_by_name)}</span></div>` : ''}
    ${data.do_created_by_name ? `<div class="header-field"><span class="header-label">Order Created by:</span> <span>${safe(data.do_created_by_name)}</span></div>` : ''}
    ${data.supervisor_name ? `<div class="header-field"><span class="header-label">Supervisor:</span> <span>${safe(data.supervisor_name)}</span></div>` : ''}
    ${emptyTruckWeightDisplay ? `<div class="header-field"><span class="header-label">Empty Wt:</span> <span>${emptyTruckWeightDisplay}</span></div>` : ''}
    ${finalTruckWeightDisplay ? `<div class="header-field"><span class="header-label">Final Wt:</span> <span>${finalTruckWeightDisplay}</span></div>` : ''}
    ${data.payment_terms ? `<div class="header-field"><span class="header-label">Payment Terms:</span> <span>${safe(data.payment_terms)}</span></div>` : ''}
    ${dueDate ? `<div class="header-field"><span class="header-label">Due Date:</span> <span>${dueDate}</span></div>` : ''}
  </div>
  ${historyHtml}
  <table>
    <thead>
      ${isDeliveryOrder ? `
        <tr>
          <th style="width: 20px;">Sr.</th>
          <th>Size/Product</th>
          <th style="width: 45px;">Qty (Pcs)</th>
          <th style="width: 60px;">Ordered Qty</th>
          <th class="text-right" style="width: 40px;">No. of Pcs.</th>
          <th class="text-right" style="width: 45px;">Average</th>
          <th class="text-right" style="width: 50px;">Total Wt.</th>
          <th class="text-right" style="width: 50px;">Rate</th>
          <th class="text-right" style="width: 60px;">GST Amt</th>
          <th class="text-right" style="width: 65px;">Amount</th>
          ${hasCoilLength ? '<th style="width: 60px;">Length</th>' : ''}
          <th style="width: 60px;">Remarks</th>
        </tr>
      ` : `
        <tr>
          <th style="width: 25px;">Sr.</th>
          <th>Size/Product</th>
          <th style="width: 45px;">HSN</th>
          <th style="width: 35px;">Pcs</th>
          <th style="width: 60px;">Assigned</th>
          <th style="width: 60px;">Actual</th>
          <th class="text-right" style="width: 60px;">Rate</th>
          <th class="text-right" style="width: 70px;">GST Amt</th>
          <th class="text-right" style="width: 80px;">Amount</th>
          ${hasCoilLength ? '<th style="width: 60px;">Length</th>' : ''}
          <th style="width: 70px;">Remark</th>
        </tr>
      `}
    </thead>
    <tbody>
      ${itemsRows || `<tr><td colspan="${isDeliveryOrder ? (hasCoilLength ? 12 : 11) : (hasCoilLength ? 11 : 10)}" align="center">No Items Found</td></tr>`}
    </tbody>
  </table>

  <div class="footer-section">
    <div class="left-col">
      <div class="total-qty-row">Total Quantity: ${safe(data.total_weight || data.total_quantity || 0)} KG</div>
      ${data.remark ? `<div style="margin-top: 4px; font-size: 8px; color: #555;"><strong>Remarks:</strong> ${safe(data.remark)}</div>` : ''}
    </div>
    <div class="right-col">
      <table class="summary-table">
        <tr><td class="label">Subtotal (Base)</td><td class="value">${formatCurrency(totalItemAmount - totalGst)}</td></tr>
        ${loading > 0 ? `<tr><td class="label">Handling Charges</td><td class="value">${formatCurrency(loading)}</td></tr>` : ''}
        ${loadingGst > 0 ? `<tr><td class="label" style="padding-left: 10px; font-size: 7px;">Handling Charges GST</td><td class="value" style="font-size: 7px;">${formatCurrency(loadingGst)}</td></tr>` : ''}
        ${showFreightRowPrint ? `<tr><td class="label" style="${freightColorPrint}">${freightLabelPrint}</td><td class="value" style="${freightColorPrint}">${freightValuePrint}</td></tr>` : ''}
        ${isFreightChargeablePrint && freightGst > 0 ? `<tr><td class="label" style="padding-left: 10px; font-size: 7px; color: #6b7280;">Freight GST</td><td class="value" style="font-size: 7px; color: #6b7280;">${formatCurrency(freightGst)}</td></tr>` : ''}
        ${totalGst > 0 ? `<tr><td class="label">Total Item GST</td><td class="value">${formatCurrency(totalGst)}</td></tr>` : ''}
        <tr class="net-total-row"><td class="label">Grand Total</td><td class="value">${formatCurrency(netTotal)}</td></tr>
      </table>
    </div>
  </div>
</div>
</body>
</html>`;

  const w = window.open('', '_blank');
  if (!w) {
    alert('Unable to open print window. Please allow popups for this site.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  try { w.document.title = documentTitle; } catch (e) { }
  setTimeout(() => { 
    try { 
      w.focus(); 
      w.print(); 
    } catch (e) { } 
  }, 300);
};

/**
 * A compact "COILS:" line for a challan item, or '' when the line has none.
 *
 * A challan travels with the truck, and for batch-tracked goods the batch numbers on it
 * are most of the point of tracking them: it is the document that says which physical
 * rolls the customer received. Rendered under the product name rather than as its own
 * column so the existing challan layout — and its A4/A6 paper settings — are untouched
 * for every ordinary line.
 *
 * Takes either shape the API returns: `coil_allocations` on a sales challan line (what
 * was consumed) or `coil_entries` on a purchase one (what arrived).
 *
 * @param {Object} item        a challan line
 * @param {number} maxListed   batches to name before summarising the rest
 * @returns {string} HTML, or '' when there is nothing to show
 */
export function coilLineHtml(item, maxListed = 6) {
  const coils = (item && (item.coil_allocations || item.coil_entries)) || [];
  if (!Array.isArray(coils) || coils.length === 0) return '';

  const esc = (v) => String(v === null || v === undefined ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const label = (c) => {
    // A sales line reports what was consumed off the coil; a purchase line reports what
    // arrived on it. Either way the operator wants batch + kg.
    const kg = c.consumed_weight_kg ?? c.take_kg ?? c.weight_kg ?? c.remaining_weight_kg;
    const kgText = kg === null || kg === undefined ? '' : ` ${Math.round(Number(kg))}kg`;
    const colour = c.colour ? ` ${esc(c.colour)}` : '';
    return `${esc(c.batch_number || c.coil_id)}${kgText}${colour}`;
  };

  const shown = coils.slice(0, maxListed).map(label).join(' · ');
  const rest = coils.length - maxListed;
  const more = rest > 0 ? ` +${rest} more` : '';

  return `<div style="font-size: 6px; color: #444; margin-top: 1px;">`
    + `<span style="font-weight: 700;">COILS:</span> ${shown}${more}</div>`;
}
